import { Link } from "react-router-dom";
import { Phone, ArrowRight, MapPin } from "lucide-react";
import { SITE, ALL_CITIES } from "@/data/site";
import { Seo } from "@/components/Seo";
import { Reveal, RevealStagger } from "@/components/Reveal";
import { GoogleMap } from "@/components/GoogleMap";
import { CtaFinal } from "@/components/CtaFinal";

const departements = [
  { name: "Dordogne (24)", desc: "Notre département d'attache. Périgueux, Bergerac, Sarlat, Ribérac et toutes les communes du Périgord." },
  { name: "Lot (46)", desc: "Cahors et le nord du Lot, à moins de deux heures de route de notre atelier." },
  { name: "Corrèze (19)", desc: "Brive-la-Gaillarde, Terrasson et le bassin de Brive." },
  { name: "Haute-Vienne (87)", desc: "Limoges et le sud du Limousin." },
  { name: "Charente (16)", desc: "Angoulême et l'est de la Charente." },
];

export default function ZonesPage() {
  return (
    <>
      <Seo
        title="Zones d'intervention Dordogne, Lot | HRC Rénovation"
        description="HRC Rénovation intervient à Périgueux, Bergerac, Sarlat, Brive, Cahors, Limoges, Angoulême. Devis gratuit 48h. ☎ 06 41 96 83 46."
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://hrcrenovation.fr/" },
            { "@type": "ListItem", position: 2, name: "Zones d'intervention" },
          ],
        }]}
      />
      <section className="pt-[120px] pb-16 lg:pb-24 bg-surface-canvas noise-overlay relative" style={{ backgroundImage: "radial-gradient(circle at 100% 0%, rgba(30,79,168,0.08) 0%, transparent 50%)" }}>
        <div className="max-w-site mx-auto px-5 lg:px-6">
          <Reveal>
            <p className="micro-label mb-4">Zones d'intervention</p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-brand-deep max-w-[820px]" style={{ letterSpacing: "-0.03em" }} data-testid="zones-h1">
              Ravalement et rénovation en Dordogne, Lot et sud Limousin
            </h1>
            <p className="mt-6 text-lg text-ink-secondary max-w-[600px]">
              Basés à Coulounieix-Chamiers, à côté de Périgueux, on se déplace dans cinq départements. Le déplacement pour le devis est toujours offert.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-28 bg-surface-canvas">
        <div className="max-w-site mx-auto px-5 lg:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <RevealStagger className="space-y-5">
            {departements.map((d) => (
              <div key={d.name} className="flex items-start gap-4 bg-white border border-line rounded-2xl p-6">
                <div className="w-11 h-11 rounded-xl bg-brand-highlight flex items-center justify-center shrink-0">
                  <MapPin size={20} className="text-brand-royal" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-lg text-brand-deep">{d.name}</h2>
                  <p className="mt-1 text-[15px] text-ink-secondary">{d.desc}</p>
                </div>
              </div>
            ))}
          </RevealStagger>
          <Reveal delay={0.15} className="lg:sticky lg:top-24">
            <GoogleMap height={520} />
            <div className="mt-6 flex flex-wrap gap-2.5">
              {ALL_CITIES.map((c) => (
                <span key={c} className="chip-city cursor-default">{c}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-surface-muted">
        <div className="max-w-[780px] mx-auto px-5 lg:px-6 text-center">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-brand-deep">Votre commune n'est pas dans la liste ?</h2>
            <p className="mt-4 text-ink-secondary">Appelez-nous. Selon le chantier, on se déplace au-delà de notre zone habituelle.</p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
              <a href={SITE.phoneHref} className="btn-base btn-metal tnum" data-testid="zones-phone-btn">
                <Phone size={18} aria-hidden="true" />
                {SITE.phone}
              </a>
              <Link to="/devis" className="btn-base btn-outline-deep">
                Demander un devis
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaFinal />
    </>
  );
}
